import * as React from 'react';

/**
 * Options for the useInfiniteScroll hook.
 */
type UseInfiniteScrollOptions = {
  /**
   * Callback function to load more data when the user scrolls near the bottom.
   * Should return a promise that resolves when loading is complete.
   */
  onLoadMore: () => Promise<void> | void;
  /**
   * Whether more data is available to load.
   * When false, the hook will stop triggering onLoadMore.
   */
  hasMore: boolean;
  /**
   * Whether data is currently being loaded.
   * When true, the hook will prevent duplicate load requests.
   */
  isLoading: boolean;
  /**
   * The root element to observe scrolling within.
   * If not provided, the window will be used.
   */
  root?: Element | null;
  /**
   * Margin around the root element's bounding box.
   * Can be used to trigger loading before reaching the exact bottom.
   * Defaults to '0px'.
   */
  rootMargin?: string;
  /**
   * Threshold for triggering the load more callback.
   * A value of 0 means trigger when the sentinel is just visible.
   * A value of 1 means trigger when the sentinel is fully visible.
   * Defaults to 0.1 (trigger when 10% visible).
   */
  threshold?: number;
  /**
   * Whether the hook is enabled.
   * When false, the hook will not trigger onLoadMore.
   * Defaults to true.
   */
  enabled?: boolean;
};

/**
 * Return type for the useInfiniteScroll hook.
 */
type UseInfiniteScrollReturn = {
  /**
   * Ref to attach to a sentinel element (e.g., a div at the bottom of the list).
   * When this element becomes visible, onLoadMore will be triggered.
   */
  sentinelRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Custom hook for implementing infinite scroll functionality.
 *
 * @param options - Configuration options for the infinite scroll hook.
 * @returns An object containing a ref to attach to a sentinel element.
 *
 * @example
 * ```tsx
 * const { sentinelRef } = useInfiniteScroll({
 *   onLoadMore: async () => {
 *     await fetchMoreData();
 *   },
 *   hasMore: hasMorePages,
 *   isLoading: isFetching,
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} {...item} />)}
 *     {hasMore && <div ref={sentinelRef} />}
 *   </div>
 * );
 * ```
 */
export const useInfiniteScroll = (options: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
  const { onLoadMore, hasMore, isLoading, root = null, rootMargin = '0px', threshold = 0.1, enabled = true } = options;

  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const isLoadingRef = React.useRef(false);

  // Update the loading ref when isLoading changes
  React.useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  React.useEffect(() => {
    if (!enabled || !hasMore || isLoadingRef.current) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    // Check if IntersectionObserver is supported
    if (typeof window.IntersectionObserver === 'undefined') {
      // Fallback to scroll event for older browsers
      const scrollElement = root || window;

      const handleScroll = () => {
        if (isLoadingRef.current || !hasMore) {
          return;
        }

        const scrollTop =
          root instanceof Element ? root.scrollTop : window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = root instanceof Element ? root.scrollHeight : document.documentElement.scrollHeight;
        const clientHeight = root instanceof Element ? root.clientHeight : window.innerHeight;

        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

        if (isNearBottom) {
          isLoadingRef.current = true;
          void Promise.resolve(onLoadMore()).finally(() => {
            isLoadingRef.current = false;
          });
        }
      };

      scrollElement.addEventListener('scroll', handleScroll, { passive: true });
      // Initial check
      handleScroll();

      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }

    // Use IntersectionObserver for modern browsers
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingRef.current && enabled) {
          isLoadingRef.current = true;
          void Promise.resolve(onLoadMore()).finally(() => {
            isLoadingRef.current = false;
          });
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    observerRef.current.observe(sentinel);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [onLoadMore, hasMore, enabled, root, rootMargin, threshold]);

  return { sentinelRef };
};
